import { ErrorHandler, inject, Injectable, Injector } from '@angular/core';

import { RouterFacade } from '../../store/router.facade';
import { SentryService } from '../sentry/sentry.service';

interface ErrorWithMessage {
  message?: string;
  error_description?: string;
  error?: string;
  stack?: string;
}

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private readonly injector = inject(Injector);
  private routerFacade: RouterFacade | null = null;
  private hasRedirectedToError = false;
  private sentryService: SentryService | null = null;

  private getRouterFacade(): RouterFacade {
    if (!this.routerFacade) {
      this.routerFacade = this.injector.get(RouterFacade);
    }
    return this.routerFacade;
  }

  private getSentryService(): SentryService {
    if (!this.sentryService) {
      this.sentryService = this.injector.get(SentryService);
    }
    return this.sentryService;
  }

  handleError(error: ErrorWithMessage | unknown): void {
    try {
      this.getSentryService().captureException(error);
    } catch (e) {
      console.error('Failed to report error to Sentry', e);
    }

    console.error('Global Error Handler:', error);

    const isKeycloakError = this.isKeycloakCriticalError(error);
    const routerFacade = this.getRouterFacade();
    const currentUrl = routerFacade.url() || '';
    const isAlreadyOnErrorPage = currentUrl.includes('/exception');

    if (isKeycloakError && !isAlreadyOnErrorPage && !this.hasRedirectedToError) {
      this.hasRedirectedToError = true;

      const errorDetails = this.getErrorDetails(error);

      routerFacade.navigate(['/exception'], {
        error: 'authentication',
        message: 'Keycloak initialization failed',
        details: errorDetails
      });

      return;
    }

    if (!isAlreadyOnErrorPage) {
      this.hasRedirectedToError = false;
    }
  }

  private isKeycloakCriticalError(error: ErrorWithMessage | unknown): boolean {
    if (!error || typeof error !== 'object') return false;

    const errorObj = error as ErrorWithMessage;
    const errorMessage = errorObj.message || errorObj.error_description || '';
    const errorType = errorObj.error || '';

    return (
      errorMessage.includes('invalid_scope') ||
      errorMessage.includes('Keycloak initialization failed') ||
      errorType === 'invalid_scope' ||
      errorMessage.includes('Keycloak') ||
      errorMessage.includes('authentication') ||
      (errorObj.stack && errorObj.stack.includes('keycloak'))
    );
  }

  private getErrorDetails(error: ErrorWithMessage | unknown): string {
    if (!error || typeof error !== 'object') return 'Unknown error';

    const errorObj = error as ErrorWithMessage;
    return errorObj.error_description || errorObj.message || 'Unknown error';
  }
}
